import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { removeFromCart, updateCartQuantity } from '../features/product/productSlice';
import { FaTimes, FaMinus, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatPrice, getStoredMarket, resolveCurrency } from '../utils/currency';
import { analyticsItemFromCartItem, centsToAnalyticsAmount, trackEvent } from '../utils/analytics';

function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart } = useSelector((state) => state.product);
  const market = getStoredMarket();

  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity > item.variant.countInStock) {
      toast.error(`Only ${item.variant.countInStock} items in stock`);
      return;
    }

    dispatch(
      updateCartQuantity({
        productId: item._id,
        variantId: item.variant._id,
        newQuantity,
      })
    );
  };

  const handleRemove = (item) => {
    const itemPrice = item.variant.discount_price || item.variant.price;

    dispatch(
      removeFromCart({
        productId: item._id,
        variantId: item.variant._id,
      })
    );
    trackEvent('remove_from_cart', {
      currency: resolveCurrency({ market }).toUpperCase(),
      value: centsToAnalyticsAmount(itemPrice * item.quantity),
      items: [analyticsItemFromCartItem(item)],
    });
    toast.info('Item removed from cart');
  };

  const formatVariantOptions = (variant) => {
    if (!variant?.optionValues || variant.optionValues.length === 0) return '';
    return variant.optionValues
      .map((opt) => `${opt.name}: ${opt.value}`)
      .join(' • ');
  };

  const cartTotal = cart.reduce((acc, item) => {
    const price = item.variant.discount_price || item.variant.price;
    return acc + price * item.quantity;
  }, 0);

  const handleCheckoutClick = () => {
    trackEvent('begin_checkout', {
      currency: resolveCurrency({ market }).toUpperCase(),
      value: centsToAnalyticsAmount(cartTotal),
      items: cart.map(analyticsItemFromCartItem),
    });
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="cart-page empty">
          <div className="empty-cart-card">
            <h2>Your cart is empty</h2>
            <p>Add some products to get started.</p>
            <Link to="/shop" className="btn btn-primary m-2 no-hover-color">
              Browse products
            </Link>
          </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="cart-heading">Shopping Cart</h1>

        <div className="cart-layout">
          <div className="cart-items">
            {cart.map((item) => {
              const itemPrice = item.variant.discount_price || item.variant.price;
              const itemKey = `${item._id}-${item.variant._id}`;

              return (
                <div className="cart-item-card" key={itemKey}>
                  <div className="cart-item-image">
                    <img
                      src={item.image?.src || '/placeholder-image.jpg'}
                      alt={item.title}
                      loading="lazy"
                    />
                  </div>

                  <div className="cart-item-body">
                    <div className="cart-item-header">
                      <div>
                        <Link to={`/product/${item._id}`} className="cart-item-title">
                          {item.title}
                        </Link>
                        {formatVariantOptions(item.variant) && (
                          <p className="cart-item-variant">
                            {formatVariantOptions(item.variant)}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="cart-item-remove"
                        onClick={() => handleRemove(item)}
                      >
                        <FaTimes />
                      </button>
                    </div>

              <div className="cart-item-price-row">
                <div>
                  <span className="label">Unit price</span>
                  <strong>{formatPrice(itemPrice, { market })}</strong>
                </div>
                <div className="cart-item-subtotal">
                  <span className="label">Subtotal</span>
                  <strong>{formatPrice(itemPrice * item.quantity, { market })}</strong>
                </div>
              </div>

                    <div className="cart-item-actions">
                      <div className="quantity-control">
                        <button
                          type="button"
                          className="btn-quantity"
                          onClick={() => handleQuantityChange(item, item.quantity - 1)}
                        >
                          <FaMinus />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          className="btn-quantity"
                          onClick={() => handleQuantityChange(item, item.quantity + 1)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="cart-summary-card">
            <h2>Order summary</h2>
            <div className="summary-line">
              <span>Subtotal</span>
              <strong>{formatPrice(cartTotal, { market })}</strong>
            </div>
            <p className="summary-note">Shipping and taxes are calculated at checkout.</p>
            <button type="button" className="btn btn-primary light" onClick={handleCheckoutClick}>
              Proceed to checkout
            </button>
            <Link to="/shop" className="btn btn-link">
              Continue shopping
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Cart;
